'use client'
import type { FC, ReactNode } from 'react'
import { useMemo } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  StackDivider,
  type StackProps,
  VStack
} from '@chakra-ui/react'
import { EmptyMessage, GrandHeading, GrandText } from 'ui'
import {
  allocateQuantityDiscount,
  formatAmount,
  getProductDiscountPrice,
  getProductOriginalPrice,
  getProductPrice,
  sumQuantityDiscountBlocks
} from 'shared-utils'
import { DiscountCodeForm } from 'forms'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { Currency } from 'ecosystem'
import { useCheckoutContext } from '../hooks'
import { resolveCheckoutCountries } from '../hooks/useCheckoutCountry'
import { useVatMode } from '../../common/hooks/useVatMode'
import { CheckoutCountrySelect } from './CheckoutCountrySelect'
import { CheckoutSummaryItem } from './CheckoutSummaryItem'
import OrderModeSelect, { type OrderModeSelectOverrides } from './OrderModeSelect'
import { useHasVatNumberNotice, VatNumberNotice } from './VatNumberNotice'

type ICheckoutSummaryProps = StackProps

export interface CheckoutSummaryOverrides extends OrderModeSelectOverrides {
  checkoutSummaryDiscount?: string
  checkoutSummarySummaryItemProducts?: string
  checkoutSummarySummaryItemServices?: string
  checkoutSummaryCompletePayment?: string
  checkoutSummaryNoProducts?: string
  checkoutSummaryDiscountMsg?: string
  checkoutSummarySummaryTotal?: string
  checkoutSummarySummaryTotalDiscount?: string
  checkoutSummaryQuantityDiscount?: string
  checkoutSummarySummaryVat?: string
  checkoutSummarySummaryNetTotal?: string
  checkoutSummarySummaryContinueToPayment?: string
  checkoutSummarySummaryContinueToReview?: string
  checkoutSummaryOrderModeHeading?: string
  checkoutSummaryCountryLabel?: string
  checkoutRemoveDiscountCode?: string
  currency?: string
}

const CheckoutSummary: FC<ICheckoutSummaryProps> = ({ ...props }) => {
  const {
    orderLines,
    createOrder,
    handleDiscountCode,
    summaryTotal,
    summaryVatAmount,
    cartState: { cartDiscountCode },
    cartTotalDiscount,
    summaryQuantityDiscount,
    handleRemoveDiscountCode,
    orderMode,
    setOrderMode,
    canUseInvoice,
    isProformaCompany,
    paymentProviderConfig,
    selectedCountryCode,
    setSelectedCountryCode
  } = useCheckoutContext()
  const overrides = useOverridesContext<keyof CheckoutSummaryOverrides>()
  const { excludeVat } = useVatMode()
  const hasVatNumberNotice = useHasVatNumberNotice(paymentProviderConfig)

  // Providers whose checkout session is created per country: the shopper picks
  // one here, it is sent with create-order, and the widget renders that
  // market's flow. The pick is also the delivery country for the DEFAULT
  // shipping provider's `countries` rules.
  const { availableCountries } = useMemo(
    () => resolveCheckoutCountries(paymentProviderConfig),
    [paymentProviderConfig]
  )
  // Only the pay-now flow creates such a session. The invoice flow has no
  // payment provider at all, so a country picker there would decide nothing.
  const showCountrySelect = availableCountries.length > 1 && orderMode === 'UPFRONT'

  const productsSummary: { id: string; label: string; value: ReactNode }[] = orderLines.map(
    (item) => {
      const discount = getProductDiscountPrice(item.product, cartDiscountCode)
      const renderCurrency = overrideText(Currency.SEK, overrides?.currency)

      // `item.unitPrice` / `item.originalPrice` are the gross values frozen for
      // BE submission. For display we resolve fresh from `item.product` so the
      // VAT toggle is reflected.
      let price = getProductPrice(item.product, excludeVat)
      let crossedPrice = getProductOriginalPrice(item.product, excludeVat)

      if (discount) {
        price = excludeVat ? discount.discountedPriceWithoutVat : discount.discountedPrice
        crossedPrice = excludeVat
          ? discount.priceUsedForDiscountWithoutVat
          : discount.priceUsedForDiscount
      }

      // The volume ladder is scored on top of whatever the code left, so the
      // line shows what the shopper actually pays for it. Its own strikethrough
      // basis is the same price before the ladder, so a line that earned only a
      // volume discount still shows what it would otherwise have cost.
      const lineAmount = sumQuantityDiscountBlocks(
        allocateQuantityDiscount(item.quantity, price, item.product.quantityDiscount)
      )
      const lineCrossed = Math.max(crossedPrice, price) * item.quantity

      return {
        id: item.product.id,
        label: `${item.quantity} x ${item.product.name}`,
        value: (
          <HStack whiteSpace="nowrap" justifyContent="flex-end">
            <Box as="span">{`${formatAmount(lineAmount)} ${renderCurrency}`}</Box>

            {lineCrossed > lineAmount && (
              <Box as="s" color="text.mild" fontSize="90%">
                {`${formatAmount(lineCrossed)} ${renderCurrency}`}
              </Box>
            )}
          </HStack>
        )
      }
    }
  )

  const servicesSummary = useMemo(() => {
    return orderLines
      .map((item) => {
        return item.services
          .filter((_s) => _s.quantity)
          .map((s) => ({
            id: s.service.id,
            label: `${s.quantity} x ${item.product.name} - ${s.service.name} ${
              s.quantity > 1
                ? `(${overrideText('w/rabatt', overrides?.checkoutSummaryDiscount)})`
                : ''
            }`,
            value: `${formatAmount(s.totalAmount)} ${overrideText(
              Currency.SEK,
              overrides?.currency
            )}`
          }))
      })
      .flat()
  }, [orderLines, overrides?.checkoutSummaryDiscount, overrides?.currency])

  const summaryItems: {
    title: string
    items: typeof productsSummary
    emptyMessage?: ReactNode
  }[] = [
    {
      title: overrideText('Produkter', overrides?.checkoutSummarySummaryItemProducts),
      items: productsSummary,
      emptyMessage: (
        <EmptyMessage
          text={overrideText(
            'Inga produkter har lagts till i varukorgen',
            overrides?.checkoutSummaryNoProducts
          )}
          textAlign="left"
        />
      )
    },
    {
      title: overrideText('Tjänster', overrides?.checkoutSummarySummaryItemServices),
      items: servicesSummary
    }
  ]
  const visibleSummaryItems = summaryItems.filter(
    ({ items, emptyMessage }) => !!items.length || !!emptyMessage
  )

  return (
    <VStack
      bg="background.surface"
      borderRadius="sm"
      divider={<StackDivider />}
      p={8}
      w="full"
      {...props}
    >
      <GrandHeading
        fontSize="lg"
        headingTag="h2"
        title={overrideText('Slutför din beställning', overrides?.checkoutSummaryCompletePayment)}
      />

      {visibleSummaryItems.map((summaryItem) => (
        <VStack key={summaryItem.title} w="100%">
          <GrandHeading fontSize="md" headingTag="h3" title={summaryItem.title} />
          {summaryItem.items.length
            ? summaryItem.items.map(({ label, value, id }) => (
                <CheckoutSummaryItem key={id} {...{ label, value }} />
              ))
            : summaryItem.emptyMessage}
        </VStack>
      ))}

      {!cartDiscountCode ? (
        <DiscountCodeForm onFormSubmit={handleDiscountCode} w="full" py={2} />
      ) : (
        <VStack spacing={4} w="full">
          <HStack justify="space-between" w="full">
            <GrandText>{cartDiscountCode.code}</GrandText>
            <GrandText align="right" fontSize="sm" fontWeight="bold">
              {cartDiscountCode.name} - {cartDiscountCode.value}%
            </GrandText>
          </HStack>

          <GrandText color="status.error" fontSize="sm">
            {overrideText(
              'Viktigt: Om priset på varan redan är rabatterat kan det bara ha effekt på varans\n' +
                '            ursprungliga pris.',
              overrides?.checkoutSummaryDiscountMsg
            )}
          </GrandText>

          <Button variant="ghost" color="gray.500" fontSize="sm" onClick={handleRemoveDiscountCode}>
            {overrideText('Ta bort rabattkod', overrides?.checkoutRemoveDiscountCode)}
          </Button>
        </VStack>
      )}

      {cartTotalDiscount ? (
        <CheckoutSummaryItem
          color="status.error"
          fontSize="lg"
          fontWeight="bold"
          label={overrideText('Rabattkod totalt', overrides?.checkoutSummarySummaryTotalDiscount)}
          value={`${formatAmount(-cartTotalDiscount)} ${overrideText(
            Currency.SEK,
            overrides?.currency
          )}`}
        />
      ) : null}

      {summaryQuantityDiscount > 0 ? (
        <CheckoutSummaryItem
          color="status.error"
          fontSize="lg"
          fontWeight="bold"
          label={overrideText('Mängdrabatt', overrides?.checkoutSummaryQuantityDiscount)}
          value={`${formatAmount(-summaryQuantityDiscount)} ${overrideText(
            Currency.SEK,
            overrides?.currency
          )}`}
        />
      ) : null}

      {summaryVatAmount > 0 ? (
        <>
          <CheckoutSummaryItem
            fontWeight="bold"
            label={overrideText('Summa exkl. moms', overrides?.checkoutSummarySummaryNetTotal)}
            value={`${formatAmount(summaryTotal - summaryVatAmount)} ${overrideText(
              Currency.SEK,
              overrides?.currency
            )}`}
          />
          <CheckoutSummaryItem
            fontWeight="bold"
            label={overrideText('Moms', overrides?.checkoutSummarySummaryVat)}
            value={`${formatAmount(summaryVatAmount)} ${overrideText(
              Currency.SEK,
              overrides?.currency
            )}`}
          />
        </>
      ) : null}

      <CheckoutSummaryItem
        fontSize="lg"
        fontWeight="bold"
        label={overrideText('Totalt', overrides?.checkoutSummarySummaryTotal)}
        value={`${formatAmount(summaryTotal)} ${overrideText(Currency.SEK, overrides?.currency)}`}
      />

      {/* Sits under the total because that is the number it explains. Gated
          rather than left to render null — this stack draws a divider around
          every element child, invisible or not. */}
      {hasVatNumberNotice ? (
        <VatNumberNotice paymentProviderConfig={paymentProviderConfig} />
      ) : null}

      {canUseInvoice ? (
        <VStack align="stretch" spacing={3} w="full" pt={2}>
          <GrandHeading
            fontSize="md"
            headingTag="h3"
            title={overrideText('Betalningssätt', overrides?.checkoutSummaryOrderModeHeading)}
          />
          <OrderModeSelect
            isProforma={isProformaCompany}
            onChange={setOrderMode}
            value={orderMode}
          />
        </VStack>
      ) : null}

      <VStack alignItems="stretch" w="100%" gap={4} mt={6}>
        {showCountrySelect && (
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="">
              {overrideText('Land', overrides?.checkoutSummaryCountryLabel)}
            </FormLabel>
            <CheckoutCountrySelect
              instanceId="checkout-country-select"
              availableCountries={availableCountries}
              value={selectedCountryCode}
              selectProps={{
                onChange: (newValue) => {
                  if (newValue?.value) {
                    setSelectedCountryCode(newValue.value)
                  }
                }
              }}
            />
          </FormControl>
        )}

        <Button
          aria-label={
            orderMode === 'INVOICE'
              ? overrideText('Granska faktura', overrides?.checkoutSummarySummaryContinueToReview)
              : overrideText(
                  'Gå vidare till betalning',
                  overrides?.checkoutSummarySummaryContinueToPayment
                )
          }
          onClick={() => {
            createOrder(showCountrySelect ? selectedCountryCode : undefined)
          }}
        >
          {orderMode === 'INVOICE'
            ? overrideText('Granska faktura', overrides?.checkoutSummarySummaryContinueToReview)
            : overrideText(
                'Gå vidare till betalning',
                overrides?.checkoutSummarySummaryContinueToPayment
              )}
        </Button>
      </VStack>
    </VStack>
  )
}

export default CheckoutSummary
